import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import UploadResumeForm from 'scenes/Form/components/UploadResumeForm';
import ProfileEditAvatarForm from 'scenes/Form/components/ProfileEditAvatarForm';
import EditCandidatePositionForm from 'scenes/Form/components/EditCandidatePositionForm';
import ProfileEditEducationForm from 'scenes/Form/components/ProfileEditEducationForm';
import ProfileAddReferenceForm from 'scenes/Form/components/ProfileAddReferenceForm';
import ProfileEditConfidentialInfoForm from 'scenes/Form/components/ProfileEditConfidentialInfoForm';
import ProfileEditPersonalInfoForm from 'scenes/Form/components/ProfileEditPersonalInfoForm';
import ProfileEditPreferencesForm from 'scenes/Form/components/ProfileEditPreferencesForm';
import ProfileEditSummaryForm from 'scenes/Form/components/ProfileEditSummaryForm';
import ProfileEditCandidateInterviewDataRequestForm from 'scenes/Form/components/ProfileEditCandidateInterviewDataRequestForm';

import { LinkBuilder } from 'helpers/link-builder';
import { FileHelper } from 'helpers/file';
import { DateHelpers } from 'helpers/date-helpers';
import { POPUP_STYLES } from 'constants/popup-styles';
import { ENDPOINT } from 'constants/endpoints';
import { POSITION_FLAG } from 'constants/profile';
import { Role } from 'helpers/user';

const action = {
  editPhoto: (state, id, { setState, callback }) => {
    FormDialog({
      compact: true,
      modeless: false,
      popupTitleStyles: {
        padding: parseInt(state.windowWidth, 10) < 480 ? '0 20px' : '0 25px',
      },
      popupInnerStyles: {
        padding: parseInt(state.windowWidth, 10) < 480 ? '0' : '0 20px',
        marginTop: '45px',
      },
      popupStyles: {
        minHeight: '30vh',
        maxWidth: '452px',
      },
      popupContentStyles: {
        paddingTop: 0,
      },
      params: { id },
      form: (actions, params) => (
        <ProfileEditAvatarForm actions={actions} params={params} />
      ),
    }).then(
      ({ PictureUrl }) => {
        if (PictureUrl) {
          setState({
            ...state,
            profile: {
              ...state.profile,
              PictureUrl: PictureUrl,
            },
          });
        }
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  deletePhoto: (state, api, id, { setState, callback }) => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete profile image?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.EDIT_CANDIDATE_PROFILE_DELETE_PICTURE,
        { id },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            setState({
              ...state,
              profile: {
                ...state.profile,
                PictureUrl: null,
              },
            });
            callback();
          },
        }
      );
    });
  },

  uploadResume: (state, id, { setState, callback }) => {
    FormDialog({
      modeless: false,
      compact: false,
      popupStyles: {
        paddingBottom: '30px',
      },
      dismissWhenClose: true,
      params: { id },
      form: (actions, params) => (
        <UploadResumeForm actions={actions} params={params} />
      ),
    }).then(
      ({ ResumeUrl, Resumes }) => {
        BasicDialog({
          bodyRender: (
            <p className="dialog__message">Resume was successfully uploaded.</p>
          ),
        });

        callback();

        if (ResumeUrl) {
          const nextState = {
            ...state,
            profile: {
              ...state.profile,
              ResumeUrl: ResumeUrl,
              ResumeUpdateData: DateHelpers.getCurrentDate(),
            },
          };

          if (Resumes) {
            nextState.profile.Resumes = [...Resumes];
          }
          setState(nextState);
        }
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addPosition: (state, user, cId, { loadData, callback }) => {
    FormDialog({
      title: 'Add Position',
      modeless: false,
      compact: true,
      params: {
        cId,
        isMasterAccount: Role.isRecruiterGbsMasterAccount(user),
      },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.POSITION}
        />
      ),
    }).then(
      () => {
        loadData();
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editPosition: (state, user, cId, id, { loadData, callback }) => {
    FormDialog({
      title: 'Edit Position',
      modeless: false,
      compact: true,
      params: {
        id,
        cId,
        isMasterAccount: Role.isRecruiterGbsMasterAccount(user),
      },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.POSITION}
        />
      ),
    }).then(
      () => {
        loadData();
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  deletePosition: (api, cId, id, { loadData, callback }) => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete this position?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(
      () => {
        api.post(
          ENDPOINT.ACTION.EDIT_CANDIDATE_DELETE_POSITION,
          { id, cId },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: () => {
              loadData();
              callback();
            },
          }
        );
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addEducation: (state, user, cId, { loadData, callback }) => {
    FormDialog({
      title: 'Add Education',
      modeless: false,
      compact: true,
      params: { cId, isMasterAccount: Role.isRecruiterGbsMasterAccount(user) },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.EDUCATION}
        />
      ),
    }).then(
      () => {
        loadData();
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editEducation: (state, user, cId, id, { loadData, callback }) => {
    FormDialog({
      title: 'Edit Education',
      modeless: false,
      compact: true,
      params: {
        id,
        cId,
        isMasterAccount: Role.isRecruiterGbsMasterAccount(user),
      },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.EDUCATION}
        />
      ),
    }).then(
      () => {
        loadData();
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editBAEducation: (state, cId, id, { loadData, callback }) => {
    FormDialog({
      title: 'Edit Education',
      modeless: false,
      compact: true,
      params: { id, cId, externalEdit: true },
      form: (actions, params) => (
        <ProfileEditEducationForm actions={actions} params={params} />
      ),
    }).then(
      () => {
        loadData();
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addReference: (state, id, { setState, callback }) => {
    FormDialog({
      title: 'Add Reference',
      modeless: false,
      compact: true,
      params: { id },
      form: (actions, params) => (
        <ProfileAddReferenceForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        if (state.profile.References.length < 4) {
          const updatedReferences = cloneDeep(state.profile.References);
          updatedReferences.push({
            ReferrerCompany: values.CompanyID.label,
            ReferrerEmail: values.Email,
            ReferrerName: values.Name,
            ReferrerPhone: values.Phone,
            ReferrerTitle: values.TitleID.label,
          });

          setState({
            ...state,
            profile: {
              ...state.profile,
              References: [...updatedReferences],
            },
          });
        }
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editConfidentialInfo: (state, user, type, id, { setState, callback }) => {
    FormDialog({
      title: `Edit ${type} ${type === 'Contact' ? 'Information' : ''}`,
      modeless: false,
      compact: true,
      popupStyles: {
        minHeight: '30vh',
      },
      params: {
        type,
        id,
        isMasterAccount: Role.isRecruiterGbsMasterAccount(user),
      },
      form: (actions, params) => (
        <ProfileEditConfidentialInfoForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        const Confidential = {
          EmailAddress2: values.EmailAddress2,
          GmatName: values.GmatID.label,
          GpaName: values.GpaID.label,
          MathSatName: values.MathSatID.label,
          PhoneNumber1: values.PhoneNumber1,
          PhoneNumber2: values.PhoneNumber2,
          ScoreName: values.ScoreID.label,
          UndergraduateMajor: values.UndergraduateMajor,
          VerbalSatName: values.VerbalSatID.label,
          WritingSatName: values.WritingSatID.label,
        };

        setState({
          ...state,
          profile: {
            ...state.profile,
            Confidential: { ...Confidential },
          },
        });

        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editPersonalInfo: (state, id, { setState, callback }) => {
    FormDialog({
      title: 'Edit Personal Information',
      modeless: false,
      compact: true,
      params: { id },
      form: (actions, params) => (
        <ProfileEditPersonalInfoForm actions={actions} params={params} />
      ),
    }).then(
      ({
        ManagePersonalAccount,
        ImmediateStartOpportunities,
        CitizenshipID,
        Languages,
      }) => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            Citizenship: [...CitizenshipID.map(item => item.label)],
            ManagePersonalAccount,
            ImmediateStartOpportunities,
            Languages: Languages
              ? Languages.map(value => ({
                  LanguageName: value.language.label,
                  LevelName: value.level.label,
                }))
              : null,
          },
        });
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editPreferences: (state, id, { setState, callback }) => {
    FormDialog({
      title: 'Edit Preferences',
      modeless: false,
      compact: true,
      params: { id },
      form: (actions, params) => (
        <ProfileEditPreferencesForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            Preferences: {
              buyside: values.Buyside.map(item => item.label),
              location: values.Location.map(item => item.label),
              fundsize: values.Fundsize.map(item => item.label),
            },
          },
        });
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editSummary: (state, id, { setState, callback }) => {
    FormDialog({
      title: 'Edit Summary',
      modeless: false,
      popupStyles: POPUP_STYLES.formPopupStyles,
      popupInnerStyles: POPUP_STYLES.formInnerStyles,
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      compact: true,
      params: { id },
      form: (actions, params) => (
        <ProfileEditSummaryForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            Summary: values.Summary || '',
          },
        });
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },

  downloadResume: (id, revision = null) => {
    const url = LinkBuilder.getCandidateResumeLink(id, revision);
    FileHelper.downloadByUrlWithChecking(
      url,
      { act: 'downloadResume', page: 0 },
      {
        companyRight: 'You do not have permission to download resumes.',
        exist: 'File not found.',
      }
    );
  },

  editInterviewData: (state, user, id, { setState, callback }) => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: {
        ...POPUP_STYLES.formPopupStyles,
        maxWidth: '540px',
      },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: {
        initialValues: {
          ...state.profile.InterviewData,
          id,
          isMasterAccount: Role.isRecruiterGbsMasterAccount(user),
        },
      },
      form: (actions, params) => (
        <ProfileEditCandidateInterviewDataRequestForm
          actions={actions}
          params={params}
        />
      ),
    }).then(
      data => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            InterviewData: { ...data },
          },
        });
        callback();
      },
      response => ResponseErrorDialog({ response })
    );
  },
};

export default action;
